import React from "react";
import { CommCenterTicketCreateParams } from "../activities/AppNavigator";
import { Page } from "../activities/AppPage";
import { API } from "../API";
import useCaregiver from "../hooks/useCaregiver";
import { isLoading, loading, Loading } from "../Loading";
import { AgencyId, AgencyMemberId, ChatRoomId } from "../messages";
import { retryableModalNetworkRequest } from "../NetworkRequest";
import { AppState } from "../system/AppState";
import { AgencyWithChatOnlyDetails } from "../system/AppState/AppAgenciesState";
import { AppCaregiver } from "../system/AppState/AppCaregiverState";
import { ChatRoom, ChatState } from "../system/AppState/ChatState";
import { HashMap } from "../utils/HashMap";
import * as anomaliesUtils from "./CaseSettings/utils/getAnomalies";

interface UseContactSupportProps {
  api: API;
  appState: AppState;
  onRequestNavigate: (page: Page) => void;
  onClickNewChatWithMedflyt: (params: CommCenterTicketCreateParams) => void;
}

export interface UseContactSupportReturnType {
  caregiver: AppCaregiver;
  agencies: HashMap<AgencyId, AgencyWithChatOnlyDetails>;
  getAnomalies: (noEncode?: boolean | undefined) => Promise<string>;
  onClickChatSupport: () => Promise<void>;
}

function useContactSupport(props: UseContactSupportProps): UseContactSupportReturnType | Loading {
  const caregiver = useCaregiver(props.appState);

  const agencies = props.appState.appAgenciesState;

  const onClickChatSupport = React.useCallback(async () => {
    if (isLoading(caregiver)) {
      return;
    }
    if (caregiver.shouldShowNewChat) {
      return props.onClickNewChatWithMedflyt.call(null, {
        type: "General",
      });
    }
    return getAgencyMemberChatRoomId(
      props.api,
      props.appState.chatState,
      AgencyMemberId.wrap(1726) /* default support guy */
    ).then((chatRoom) => {
      if (chatRoom !== null) {
        props.onRequestNavigate.call(null, {
          type: "Chat",
          chatRoomId: chatRoom,
          linkToVisit: false,
        });
      }
    });
  }, [
    caregiver,
    props.api,
    props.appState.chatState,
    props.onClickNewChatWithMedflyt,
    props.onRequestNavigate,
  ]);

  return React.useMemo(() => {
    if (isLoading(caregiver) || isLoading(agencies)) {
      return loading();
    }

    return {
      caregiver: caregiver,
      agencies: agencies.agencies,
      getAnomalies: (noEncode?: boolean) => anomaliesUtils.getAnomalies(props.api, noEncode),
      onClickChatSupport: onClickChatSupport,
    };
  }, [agencies, caregiver, onClickChatSupport, props.api]);
}

async function getAgencyMemberChatRoomId(
  api: API,
  chatState: ChatState,
  agencyMemberId: AgencyMemberId
): Promise<ChatRoomId | null> {
  const chatRooms = chatState.getAppChatState().chatRooms;

  if (isLoading(chatRooms)) {
    return null;
  }

  const chatRoomId = lookupAgencyMemberChatRoom(chatRooms, agencyMemberId);

  if (chatRoomId !== null) {
    return chatRoomId;
  }

  const newChatRoom = await retryableModalNetworkRequest(
    () => chatState.createChatRoom(() => api.getAgencyMemberChatRoom(agencyMemberId)),
    true
  );

  if (newChatRoom === null) {
    return null;
  }

  return newChatRoom.id;
}

function lookupAgencyMemberChatRoom(
  chatRooms: HashMap<ChatRoomId, ChatRoom>,
  agencyMemberId: AgencyMemberId
): ChatRoomId | null {
  for (const chatRoom of chatRooms.values()) {
    if (chatRoom.chatRoomType.type === "AgencyMemberCaregiverChat") {
      if (chatRoom.chatRoomType.agencyMemberId === agencyMemberId) {
        return chatRoom.id;
      }
    }
  }

  return null;
}

export default useContactSupport;
